import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useShow } from "@/contexts/ShowContext";
import AssetsEditor from "@/components/AssetsEditor";
import { DEFAULT_ASSET_SORT } from "@/components/AssetsEditor/constants";

const STORAGE_PREFIX = "assetsEditor.settings";

const STATUS_KEYS = ["draft", "approved", "rejected"];

const loadSettings = (showId) => {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}.${showId}`);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
};

const saveSettings = (showId, patch) => {
  try {
    const key = `${STORAGE_PREFIX}.${showId}`;
    const raw = localStorage.getItem(key);
    const current = raw ? JSON.parse(raw) || {} : {};
    localStorage.setItem(key, JSON.stringify({ ...current, ...patch }));
  } catch {
    // ignore quota or serialization errors
  }
};

// Parse a comma-separated query param into a string array. Returns null when
// the param is absent so callers can fall back to a default.
const parseList = (searchParams, key) => {
  if (!searchParams.has(key)) return null;
  return (searchParams.get(key) || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

// Decode the ?status= param (comma list of enabled statuses) into the
// { draft, approved, rejected } shape the editor expects.
const parseStatus = (searchParams) => {
  const list = parseList(searchParams, "status");
  if (!list) return null;
  return STATUS_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: list.includes(key) }),
    {},
  );
};

export default function ShowAssetsPage() {
  const { show } = useShow();
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate settings once on mount. URL params take precedence over saved
  // settings so deep links / refresh / back-forward keep working. For the
  // folder specifically we distinguish:
  //   - param missing  → fall back to saved folder
  //   - `?folder=`     → explicit root (overrides saved)
  //   - `?folder=<id>` → that folder
  const [initial] = useState(() => {
    const saved = loadSettings(show._id);
    const urlFolder = searchParams.get("folder");
    const urlPage = parseInt(searchParams.get("page"), 10);
    const urlTags = parseList(searchParams, "tags");
    const urlStatus = parseStatus(searchParams);
    const urlMedia = parseList(searchParams, "media");
    return {
      folder: urlFolder !== null ? urlFolder || null : (saved.folder ?? null),
      categories:
        urlTags ??
        (Array.isArray(saved.categories) ? saved.categories : undefined),
      status: urlStatus ?? saved.status,
      media: urlMedia ?? undefined,
      archived: searchParams.get("archived") === "true",
      characterFilter:
        searchParams.get("characters") || saved.characterFilter || "",
      sort: searchParams.get("sort") || saved.sort || DEFAULT_ASSET_SORT,
      page:
        Number.isFinite(urlPage) && urlPage > 0
          ? urlPage
          : typeof saved.page === "number"
            ? saved.page
            : 1,
      zoom: typeof saved.zoom === "number" ? saved.zoom : undefined,
    };
  });

  // If the URL had no `folder` param at all but we restored one from storage,
  // mirror it onto the URL so refresh and back/forward keep working. We check
  // for the param being *missing* (=== null) rather than empty, so deep
  // links like `?folder=` keep meaning "explicit root".
  useEffect(() => {
    const urlFolder = searchParams.get("folder");
    if (urlFolder === null && initial.folder) {
      const next = new URLSearchParams(searchParams);
      next.set("folder", initial.folder);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Merge a mutation into the current URL search params. Uses the functional
  // updater so concurrent param updates (e.g. folder + page during "Go to
  // folder") don't clobber each other.
  const updateParams = (mutate, opts) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      mutate(next);
      return next;
    }, opts);
  };

  const handleFolderChange = (folderId) => {
    // Folder navigation is the meaningful history boundary (replace: false)
    // and it resets paging, so drop a stale ?page= here. Keep the folder
    // param around (empty when at root) so the URL is always an explicit
    // pointer to *where* the user is, overriding the saved folder on refresh.
    updateParams(
      (next) => {
        next.set("folder", folderId || "");
        next.delete("page");
      },
      { replace: false },
    );
    saveSettings(show._id, { folder: folderId ?? null });
  };

  const handlePageChange = (page) => {
    updateParams(
      (next) => {
        if (page > 1) next.set("page", String(page));
        else next.delete("page");
      },
      { replace: true },
    );
    saveSettings(show._id, { page });
  };

  const handleCategoriesChange = (categories) => {
    const everyTagSelected =
      !Array.isArray(categories) ||
      categories.length >= show.asset_categories.length;

    updateParams(
      (next) => {
        if (everyTagSelected) {
          next.delete("tags");
        } else {
          next.set("tags", categories.join(","));
        }
      },
      { replace: true },
    );

    // Encode "all selected" as absent, never as the ids that exist today.
    //
    // This value (and the `tags` search param above) only seeds
    // selectedFilterTags at mount; absent means "re-derive from
    // show.asset_categories". Either way the editor sends the whole list on
    // every fetch, so what we persist here decides what that URL looks like
    // tomorrow:
    //
    //   undefined   -> /assets/items?tags=t1,...,t23&showUntagged=true   re-derived
    //   [t1..t22]   -> /assets/items?tags=t1,...,t22&showUntagged=true   frozen
    //
    // The API reads `tags` as a whitelist: an asset comes back only if one of
    // its tags is listed, or it has none at all. So the frozen form quietly
    // omits t23 — tag an asset with it and the asset matches neither branch
    // and drops out of the grid.
    saveSettings(show._id, {
      categories: everyTagSelected ? undefined : categories,
    });
  };

  const handleStatusChange = (status) => {
    updateParams(
      (next) => {
        const enabled = STATUS_KEYS.filter((key) => status?.[key]);
        if (enabled.length < STATUS_KEYS.length) {
          next.set("status", enabled.join(","));
        } else {
          next.delete("status");
        }
      },
      { replace: true },
    );
    saveSettings(show._id, { status });
  };

  const handleMediaChange = (media, allSelected) => {
    updateParams(
      (next) => {
        if (allSelected) next.delete("media");
        else next.set("media", (media || []).join(","));
      },
      { replace: true },
    );
  };

  const handleArchivedChange = (archived) => {
    updateParams(
      (next) => {
        if (archived) next.set("archived", "true");
        else next.delete("archived");
      },
      { replace: true },
    );
  };

  const handleSortChange = (sort) => {
    updateParams(
      (next) => {
        if (sort === DEFAULT_ASSET_SORT) next.delete("sort");
        else next.set("sort", sort);
      },
      { replace: true },
    );
    saveSettings(show._id, { sort });
  };

  const handleCharacterFilterChange = (characterFilter) => {
    updateParams(
      (next) => {
        if (characterFilter) next.set("characters", characterFilter);
        else next.delete("characters");
      },
      { replace: true },
    );
    saveSettings(show._id, { characterFilter });
  };

  // `folder` prop semantics in AssetsEditor: null = root. Honor the URL
  // when present (`?folder=` → null root), fall back to saved.
  const urlFolderParam = searchParams.get("folder");
  const folderForEditor =
    urlFolderParam !== null ? urlFolderParam || null : initial.folder || null;

  return (
    <AssetsEditor
      showAssetsList
      folder={folderForEditor}
      categories={initial.categories}
      status={initial.status}
      media={initial.media}
      archived={initial.archived}
      characterFilter={initial.characterFilter}
      sort={initial.sort}
      page={initial.page}
      zoom={initial.zoom}
      onFolderChange={handleFolderChange}
      onCategoriesChange={handleCategoriesChange}
      onStatusChange={handleStatusChange}
      onMediaChange={handleMediaChange}
      onArchivedChange={handleArchivedChange}
      onCharacterFilterChange={handleCharacterFilterChange}
      onSortChange={handleSortChange}
      onPageChange={handlePageChange}
      onZoomChange={(zoom) => saveSettings(show._id, { zoom })}
    />
  );
}
